import { Controller } from "@hotwired/stimulus"
import { app } from "theme/stimulus"

app.register(
  "stockists-map",
  class extends Controller {
    static targets = [
      "map",
      "list",
      "listItems",
      "item",
      "data",
      "searchInput",
      "searchSubmit",
      "searchClear",
      "filterInput",
      "distance",
      "status",
      "emptyState",
    ]
    static values = {
      defaultLat: { type: Number, default: 20 },
      defaultLng: { type: Number, default: 0 },
      defaultZoom: { type: Number, default: 2 },
      glyphs: { type: String, default: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf" },
      labelFont: { type: String, default: "" },
      markerUrl: { type: String, default: "" },
      markerSize: { type: Number, default: 32 },
    }

    static maplibrePromise = null

    async connect() {
      if (!this.hasMapTarget || !this.hasDataTarget) return
      this.stockists = JSON.parse(this.dataTarget.textContent || "[]")
      this.searchOrigin = null
      this.filterQuery = ""
      await this.loadMapLibre()
      this.initMap()
      this.map.on("load", () => {
        this.plotPins()
        this.fitToPins()
        if (this.hasItemTargets) {
          this.itemTargets.forEach((item, i) => (item.dataset.originalIndex = String(i)))
        }
        this.recompute()
        const attrib = this.element.querySelector(".maplibregl-ctrl-attrib")
        if (attrib) attrib.classList.remove("maplibregl-compact-show")
      })
    }

    disconnect() {
      if (this.map) {
        this.map.remove()
        this.map = null
      }
      if (this.originMarker) this.originMarker = null
    }

    loadMapLibre() {
      if (window.maplibregl) return Promise.resolve()
      if (this.constructor.maplibrePromise) return this.constructor.maplibrePromise

      const cssId = "maplibre-css"
      if (!document.getElementById(cssId)) {
        const link = document.createElement("link")
        link.id = cssId
        link.rel = "stylesheet"
        link.href = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css"
        document.head.appendChild(link)
      }

      this.constructor.maplibrePromise = new Promise((resolve, reject) => {
        const script = document.createElement("script")
        script.src = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js"
        script.onload = () => resolve()
        script.onerror = reject
        document.head.appendChild(script)
      })
      return this.constructor.maplibrePromise
    }

    buildStyle() {
      const labelFont = this.labelFontValue ? [this.labelFontValue] : null
      const layers = [
        { id: "bg", type: "background", paint: { "background-color": "#f2f2f2" } },
        { id: "water", type: "fill", source: "openmaptiles", "source-layer": "water", paint: { "fill-color": "#ffffff" } },
        {
          id: "roads-minor",
          type: "line",
          source: "openmaptiles",
          "source-layer": "transportation",
          filter: ["!in", "class", "motorway", "trunk", "primary", "secondary"],
          paint: {
            "line-color": "#bcbcbc",
            "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.2, 14, 0.6, 17, 1.6],
          },
        },
        {
          id: "roads-major",
          type: "line",
          source: "openmaptiles",
          "source-layer": "transportation",
          filter: ["in", "class", "motorway", "trunk", "primary", "secondary"],
          paint: {
            "line-color": "#a8a8a8",
            "line-width": ["interpolate", ["linear"], ["zoom"], 6, 0.4, 11, 1.2, 14, 2.4, 17, 4],
          },
        },
      ]

      if (labelFont) {
        layers.push({
          id: "place-labels",
          type: "symbol",
          source: "openmaptiles",
          "source-layer": "place",
          minzoom: 4,
          filter: ["in", "class", "city", "town", "village", "neighbourhood", "suburb"],
          layout: {
            "text-field": ["get", "name:latin"],
            "text-font": labelFont,
            "text-size": ["interpolate", ["linear"], ["zoom"], 6, 10, 14, 14],
            "text-transform": "uppercase",
          },
          paint: {
            "text-color": "#000000",
          },
        })
      }

      return {
        version: 8,
        sources: {
          openmaptiles: { type: "vector", url: "https://tiles.openfreemap.org/planet" },
        },
        glyphs: this.glyphsValue,
        layers,
      }
    }

    initMap() {
      this.map = new window.maplibregl.Map({
        container: this.mapTarget,
        style: this.buildStyle(),
        center: [this.defaultLngValue, this.defaultLatValue],
        zoom: Math.max(this.defaultZoomValue - 1, 0),
        attributionControl: false,
        cooperativeGestures: true,
      })

      this.map.addControl(
        new window.maplibregl.AttributionControl({
          compact: true,
          customAttribution: "© OpenStreetMap · OpenFreeMap",
        }),
        "bottom-right",
      )
      this.map.on("moveend", () => this.recompute())
    }

    plotPins() {
      this.markers = new Map()
      this.stockists.forEach((s) => {
        const lat = parseFloat(s.lat)
        const lng = parseFloat(s.lng)
        if (isNaN(lat) || isNaN(lng)) return
        const popup = new window.maplibregl.Popup({ offset: 28, closeButton: false }).setHTML(this.buildPopup(s))
        const element = this.markerUrlValue ? this.buildMarkerElement() : null
        const markerOpts = element ? { element } : { color: "#000000" }
        const marker = new window.maplibregl.Marker(markerOpts).setLngLat([lng, lat]).setPopup(popup).addTo(this.map)
        if (element) {
          popup.on("open", () => element.classList.add("is-active"))
          popup.on("close", () => element.classList.remove("is-active"))
        }
        this.markers.set(s.handle, marker)
      })
    }

    buildMarkerElement() {
      const wrap = document.createElement("div")
      wrap.className = "stockists-marker"
      const size = this.markerSizeValue
      wrap.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;border-radius:50%;display:flex;align-items:center;justify-content:center;`
      const img = document.createElement("img")
      img.src = this.markerUrlValue
      img.alt = ""
      img.style.cssText = "width:55%;height:55%;display:block;pointer-events:none;"
      wrap.appendChild(img)
      return wrap
    }

    buildPopup(s) {
      const esc = (str) =>
        String(str || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
      const str = (v) => {
        if (v == null) return ""
        if (typeof v === "string") return v
        if (typeof v === "object") return v.value || v.text || ""
        return String(v)
      }
      const parts = []
      const name = str(s.name)
      if (name) parts.push(`<div class="popup-name">${esc(name)}</div>`)

      const address = str(s.address)
      const country = str(s.country)
      if (address || country) {
        const addrLines = [address, country].filter(Boolean).map((l) => `<div>${esc(l)}</div>`).join("")
        parts.push(`<div class="popup-address">${addrLines}</div>`)
      }

      const links = []
      const website = str(s.website)
      if (website) {
        const host = website.replace(/^https?:\/\//, "").split("/")[0]
        links.push(`<a href="${esc(website)}" target="_blank" rel="noopener">${esc(host)}</a>`)
      }
      const phone = str(s.phone)
      if (phone) links.push(`<a href="tel:${esc(phone.replace(/\s+/g, ""))}">${esc(phone)}</a>`)
      const lat = parseFloat(s.lat)
      const lng = parseFloat(s.lng)
      if (!isNaN(lat) && !isNaN(lng)) {
        const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
        links.push(`<a href="${esc(dirUrl)}" target="_blank" rel="noopener">Directions</a>`)
      }
      if (links.length) parts.push(`<div class="popup-links">${links.join("")}</div>`)

      const hours = str(s.hours)
      if (hours) parts.push(`<div class="popup-hours">${esc(hours).replace(/\n/g, "<br>")}</div>`)

      return parts.join("")
    }

    fitToPins(handles) {
      if (!this.markers || this.markers.size === 0) return
      const visible = handles
        ? handles.map((h) => this.markers.get(h)).filter(Boolean)
        : Array.from(this.markers.values())
      if (visible.length === 0) return
      const bounds = new window.maplibregl.LngLatBounds()
      visible.forEach((m) => bounds.extend(m.getLngLat()))
      this.map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 })
    }

    filterList(event) {
      this.filterQuery = (event.currentTarget.value || "").trim().toLowerCase()
      this.recompute()
    }

    itemMatches(item) {
      if (!this.filterQuery) return true
      return (item.dataset.search || "").includes(this.filterQuery)
    }

    recompute() {
      if (!this.map || !this.markers || !this.hasItemTargets) return
      const bounds = this.map.getBounds()
      const textMatchHandles = []
      let visibleCount = 0

      this.itemTargets.forEach((item) => {
        const lat = parseFloat(item.dataset.lat)
        const lng = parseFloat(item.dataset.lng)
        const textMatch = this.itemMatches(item)
        const inBounds = !isNaN(lat) && !isNaN(lng) && bounds.contains([lng, lat])
        const visible = textMatch && inBounds
        item.classList.toggle("hidden", !visible)
        if (textMatch) textMatchHandles.push(item.dataset.handle)
        if (visible) visibleCount++
      })

      this.markers.forEach((marker, handle) => {
        const shouldShow = textMatchHandles.includes(handle)
        const onMap = !!marker.getElement().parentNode
        if (shouldShow && !onMap) marker.addTo(this.map)
        else if (!shouldShow && onMap) marker.remove()
      })

      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.toggle("hidden", visibleCount > 0)
      }
    }

    async searchNearby(event) {
      event.preventDefault()
      if (!this.hasSearchInputTarget) return
      const query = this.searchInputTarget.value.trim()
      if (!query) return

      this.setStatus("Searching…", false)
      this.searchSubmitTarget.disabled = true

      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`
        const res = await fetch(url, { headers: { Accept: "application/json" } })
        if (!res.ok) throw new Error("Geocoding failed")
        const data = await res.json()
        if (!data.length) {
          this.setStatus(`No location found for "${query}".`, true)
          return
        }
        const lat = parseFloat(data[0].lat)
        const lng = parseFloat(data[0].lon)
        const display = data[0].display_name || query

        this.searchOrigin = { lat, lng, label: display }
        this.placeOriginMarker(lat, lng, display)
        this.map.flyTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 11), duration: 600 })
        this.sortByDistance(this.searchOrigin)
        this.setStatus(`Showing stockists near ${display}.`, true)
        this.searchClearTarget.classList.remove("hidden")
      } catch (err) {
        this.setStatus("Search failed. Try again.", true)
      } finally {
        this.searchSubmitTarget.disabled = false
      }
    }

    useMyLocation() {
      if (!("geolocation" in navigator)) {
        this.setStatus("Geolocation not supported in this browser.", true)
        return
      }
      this.setStatus("Locating…", true)
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude
          const lng = pos.coords.longitude
          const label = "Your location"
          this.searchOrigin = { lat, lng, label }
          this.placeOriginMarker(lat, lng, label)
          this.map.flyTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 11), duration: 600 })
          this.sortByDistance(this.searchOrigin)
          this.setStatus("Showing stockists near your location.", true)
          if (this.hasSearchClearTarget) this.searchClearTarget.classList.remove("hidden")
        },
        (err) => {
          const msg = err && err.code === err.PERMISSION_DENIED ? "Location permission denied." : "Could not get your location."
          this.setStatus(msg, true)
        },
        { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 },
      )
    }

    clearSearch() {
      this.searchOrigin = null
      if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
      if (this.originMarker) {
        this.originMarker.remove()
        this.originMarker = null
      }
      if (this.hasDistanceTargets) {
        this.distanceTargets.forEach((el) => {
          el.textContent = ""
          el.classList.add("hidden")
        })
      }
      if (this.hasItemTargets) this.restoreOriginalOrder()
      this.setStatus("", true)
      if (this.hasSearchClearTarget) this.searchClearTarget.classList.add("hidden")
      this.recompute()
    }

    placeOriginMarker(lat, lng, label) {
      if (this.originMarker) this.originMarker.remove()
      const el = document.createElement("div")
      el.style.cssText = "width:14px;height:14px;border-radius:50%;background:#000;border:3px solid #fff;box-shadow:0 0 0 2px #000;"
      this.originMarker = new window.maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .setPopup(new window.maplibregl.Popup({ offset: 16, closeButton: false }).setHTML(`<strong>You</strong><br>${this.escape(label)}`))
        .addTo(this.map)
    }

    escape(s) {
      return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    }

    sortByDistance(origin) {
      if (!this.hasItemTargets || !this.hasListItemsTarget) return
      const enriched = this.itemTargets.map((item) => {
        const lat = parseFloat(item.dataset.lat)
        const lng = parseFloat(item.dataset.lng)
        const distance = this.haversine(origin.lat, origin.lng, lat, lng)
        return { item, distance }
      })

      enriched.sort((a, b) => a.distance - b.distance)
      const frag = document.createDocumentFragment()
      enriched.forEach(({ item, distance }) => {
        const distEl = item.querySelector('[data-stockists-map-target="distance"]')
        if (distEl) {
          distEl.textContent = this.formatDistance(distance)
          distEl.classList.remove("hidden")
        }
        frag.appendChild(item)
      })
      this.listItemsTarget.appendChild(frag)
      this.recompute()
    }

    restoreOriginalOrder() {
      if (!this.hasItemTargets || !this.hasListItemsTarget) return
      const original = this.itemTargets.slice().sort((a, b) => {
        const ai = parseInt(a.dataset.originalIndex || "0", 10)
        const bi = parseInt(b.dataset.originalIndex || "0", 10)
        return ai - bi
      })
      const frag = document.createDocumentFragment()
      original.forEach((item) => frag.appendChild(item))
      this.listItemsTarget.appendChild(frag)
    }

    haversine(lat1, lng1, lat2, lng2) {
      const R = 6371
      const toRad = (deg) => (deg * Math.PI) / 180
      const dLat = toRad(lat2 - lat1)
      const dLng = toRad(lng2 - lng1)
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
      return 2 * R * Math.asin(Math.sqrt(a))
    }

    formatDistance(km) {
      if (km < 1) return `${Math.round(km * 1000)} m`
      if (km < 10) return `${km.toFixed(1)} km`
      return `${Math.round(km)} km`
    }

    setStatus(text, visible) {
      if (!this.hasStatusTarget) return
      this.statusTarget.textContent = text
      this.statusTarget.classList.toggle("hidden", !visible || !text)
    }

    focus(event) {
      const btn = event.currentTarget
      const li = btn.closest('[data-stockists-map-target="item"]')
      if (!li) return
      const handle = li.dataset.handle
      const lat = parseFloat(li.dataset.lat)
      const lng = parseFloat(li.dataset.lng)
      if (!this.map || isNaN(lat) || isNaN(lng)) return
      this.map.flyTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 13), duration: 600 })
      const marker = this.markers && this.markers.get(handle)
      if (marker) marker.togglePopup()
    }

    stopPropagation(event) {
      event.stopPropagation()
    }

    zoomIn() {
      if (this.map) this.map.zoomIn()
    }

    zoomOut() {
      if (this.map) this.map.zoomOut()
    }
  },
)
